import { FilterOutlined, LinkOutlined } from '@ant-design/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Badge,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  List,
  Pagination,
  Select,
  Switch,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { apiErrorMessage } from '../api/client';
import { categoriesApi, operationsApi, skladsApi, storesApi } from '../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../api/queryKeys';
import { OPERATION_TYPES } from '../api/types';
import type { ListOperationsParams, Operation, OperationType } from '../api/types';
import { ModalSelect } from '../components/ModalSelect';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/PageStates';
import { OperationEditModal } from '../components/OperationEditModal';
import { OpTypeTag } from '../components/OpTypeTag';
import { ActorLine } from '../components/ActorLine';
import { PaymentMethodTag } from '../components/PaymentMethodTag';
import { useAuth } from '../auth/AuthContext';
import { useStoreContext } from '../context/StoreContext';
import { CASH_RECEIVED, OWED_TO_OWNER, OWNER_OWES } from '../lib/balanceColors';
import { formatKopeks } from '../lib/money';

const PAGE_SIZE = 50;

interface Filters {
  type?: OperationType;
  storeId?: string;
  skladId?: string;
  categoryId?: string;
  range?: [Dayjs | null, Dayjs | null] | null;
  includeDeleted: boolean;
}

const EMPTY_FILTERS = { includeDeleted: false } satisfies Filters;

/**
 * Journal amounts follow the owner-perspective convention (lib/balanceColors):
 * ISSUE grows a store's debt to the owner (green), STORE_PAYMENT is cash in
 * hand (blue), everything else is the owner's money going out (red).
 */
function amountColor(type: OperationType): string {
  switch (type) {
    case 'ISSUE':
      return OWED_TO_OWNER;
    case 'STORE_PAYMENT':
      return CASH_RECEIVED;
    default:
      return OWNER_OWES;
  }
}

/**
 * /operations journal (STEP-07): paged list (date DESC), filter drawer, the
 * «show deleted» toggle (struck-through rows with a restore button), row tap
 * opens the shared edit modal. The global store context pre-applies the
 * store filter and hides the store control.
 */
export function OperationsPage(): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId: ctxStoreId, storeIds: ctxStoreIds, scopeStores } = useStoreContext();
  const { isStoreRestricted } = useAuth();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Operation | null>(null);
  const [filterForm] = Form.useForm<Filters>();

  const params: ListOperationsParams = {
    ...(filters.type ? { type: filters.type } : {}),
    // Global scope first: one store wins outright; a user-scope SET narrows the
    // journal and the drawer's store filter may pick inside it.
    ...(ctxStoreId
      ? { storeId: ctxStoreId }
      : filters.storeId
        ? { storeId: filters.storeId }
        : ctxStoreIds
          ? { storeIds: ctxStoreIds }
          : {}),
    ...(filters.skladId ? { skladId: filters.skladId } : {}),
    ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
    ...(filters.range?.[0] ? { from: filters.range[0].format('YYYY-MM-DD') } : {}),
    ...(filters.range?.[1] ? { to: filters.range[1].format('YYYY-MM-DD') } : {}),
    ...(filters.includeDeleted ? { includeDeleted: true } : {}),
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.operations(params as Record<string, unknown>),
    queryFn: () => operationsApi.list(params),
    placeholderData: keepPreviousData,
  });

  const skladsQuery = useQuery({
    queryKey: queryKeys.sklads(true),
    queryFn: () => skladsApi.list(true),
    enabled: drawerOpen,
  });
  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories(true),
    queryFn: () => categoriesApi.list(true),
    enabled: drawerOpen,
  });
  const allStoresQuery = useQuery({
    queryKey: queryKeys.stores(true),
    queryFn: () => storesApi.list(true),
    enabled: drawerOpen && !ctxStoreId && !ctxStoreIds,
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => operationsApi.restore(id),
    onSuccess: () => {
      message.success(t('ledger.restored'));
      invalidateMoneyData(queryClient);
    },
    onError: (error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const activeFilterCount =
    (filters.type ? 1 : 0) +
    (!ctxStoreId && filters.storeId ? 1 : 0) +
    (filters.skladId ? 1 : 0) +
    (filters.categoryId ? 1 : 0) +
    (filters.range?.[0] || filters.range?.[1] ? 1 : 0) +
    (filters.includeDeleted ? 1 : 0);

  const applyFilters = (values: Filters): void => {
    setFilters({ ...values, includeDeleted: values.includeDeleted ?? false });
    setPage(1);
    setDrawerOpen(false);
  };

  const data = query.data;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Text type="secondary">
          {data ? t('journal.totalCount', { count: data.total }) : ' '}
        </Typography.Text>
        <Badge count={activeFilterCount} size="small">
          <Button icon={<FilterOutlined />} onClick={() => setDrawerOpen(true)}>
            {t('journal.filters')}
          </Button>
        </Badge>
      </div>

      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : data.items.length === 0 ? (
        <EmptyBlock description={t('journal.empty')} />
      ) : (
        <Card size="small" styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
          <List
            dataSource={data.items}
            renderItem={(operation) => {
              const deleted = operation.deletedAt !== null;
              // A накладная-backed row is document-owned — it opens the накладная
              // instead of the raw editor, and can never be edited/restored here.
              const fromShipment = operation.shipmentId != null;
              const names = [operation.store?.name, operation.sklad?.name, operation.category?.name]
                .filter(Boolean)
                .join(' · ');
              const onRowClick = fromShipment
                ? () => navigate(`/shipments/${operation.shipmentId}`)
                : deleted
                  ? undefined
                  : () => setEditing(operation);
              return (
                <List.Item
                  onClick={onRowClick}
                  style={{
                    paddingInline: 4,
                    alignItems: 'flex-start',
                    cursor: onRowClick ? 'pointer' : 'default',
                    opacity: deleted ? 0.55 : 1,
                  }}
                  extra={
                    fromShipment ? (
                      <Button type="link" onClick={() => navigate(`/shipments/${operation.shipmentId}`)}>
                        {t('shipments.openDoc')}
                      </Button>
                    ) : deleted ? (
                      <Button
                        loading={restoreMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreMutation.mutate(operation.id);
                        }}
                      >
                        {t('common.restore')}
                      </Button>
                    ) : undefined
                  }
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textDecoration: deleted ? 'line-through' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <Typography.Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {dayjs(operation.date).format('DD.MM.YY')}
                      </Typography.Text>
                      <OpTypeTag type={operation.type} />
                      <PaymentMethodTag method={operation.paymentMethod} />
                      {operation.pairId ? (
                        <Tag icon={<LinkOutlined />} color="purple" style={{ marginInlineEnd: 0 }}>
                          {t('common.pairTag')}
                        </Tag>
                      ) : null}
                      {deleted ? <Tag color="default">{t('common.deletedTag')}</Tag> : null}
                    </div>
                    {names ? (
                      <Typography.Text style={{ display: 'block', fontSize: 13 }} ellipsis>
                        {names}
                      </Typography.Text>
                    ) : null}
                    {operation.comment ? (
                      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 13 }} ellipsis>
                        {operation.comment}
                      </Typography.Text>
                    ) : null}
                    <ActorLine
                      enteredBy={operation.enteredByName}
                      approvedBy={operation.approvedByName}
                    />
                  </div>
                  <Typography.Text
                    strong
                    style={{
                      marginInlineStart: 8,
                      whiteSpace: 'nowrap',
                      color: amountColor(operation.type),
                      textDecoration: deleted ? 'line-through' : 'none',
                    }}
                  >
                    {formatKopeks(operation.amount)}
                  </Typography.Text>
                </List.Item>
              );
            }}
          />
        </Card>
      )}

      {data && data.total > PAGE_SIZE ? (
        <Pagination
          current={page}
          pageSize={PAGE_SIZE}
          total={data.total}
          onChange={setPage}
          showSizeChanger={false}
          style={{ textAlign: 'center' }}
        />
      ) : null}

      <Drawer
        title={t('journal.filters')}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="bottom"
        height="80%"
      >
        <Form<Filters>
          form={filterForm}
          layout="vertical"
          initialValues={EMPTY_FILTERS}
          onFinish={applyFilters}
        >
          <Form.Item name="type" label={t('journal.type')}>
            <Select
              size="large"
              allowClear
              placeholder={t('journal.typePlaceholder')}
              options={OPERATION_TYPES.filter((type) => !isStoreRestricted || type !== 'SKLAD_PAYMENT').map(
                (type) => ({ value: type, label: t(`opType.${type}`) }),
              )}
            />
          </Form.Item>
          {!ctxStoreId ? (
            <Form.Item name="storeId" label={t('common.store')}>
              <ModalSelect
                size="large"
                allowClear
                placeholder={t('journal.storePlaceholder')}
                title={t('common.store')}
                loading={allStoresQuery.isLoading}
                options={(ctxStoreIds ? scopeStores : (allStoresQuery.data ?? scopeStores)).map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
              />
            </Form.Item>
          ) : null}
          {!isStoreRestricted ? (
            <Form.Item name="skladId" label={t('common.sklad')}>
              <ModalSelect
                size="large"
                allowClear
                placeholder={t('journal.skladPlaceholder')}
                title={t('common.sklad')}
                loading={skladsQuery.isLoading}
                options={(skladsQuery.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
          ) : null}
          <Form.Item name="categoryId" label={t('common.category')}>
            <Select
              size="large"
              allowClear
              placeholder={t('journal.categoryPlaceholder')}
              loading={categoriesQuery.isLoading}
              options={(categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item name="range" label={t('journal.period')}>
            <DatePicker.RangePicker
              size="large"
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              allowEmpty={[true, true]}
              inputReadOnly
            />
          </Form.Item>
          <Form.Item name="includeDeleted" label={t('journal.showDeleted')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="large"
              block
              onClick={() => {
                filterForm.resetFields();
                applyFilters(EMPTY_FILTERS);
              }}
            >
              {t('common.reset')}
            </Button>
            <Button type="primary" size="large" block onClick={() => filterForm.submit()}>
              {t('common.apply')}
            </Button>
          </div>
        </Form>
      </Drawer>

      <OperationEditModal operation={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
